import { FileUploadOutlined } from "@mui/icons-material";
import Tab from "@mui/material/Tab";
import Tabs from "@mui/material/Tabs";
import React, { ReactElement, useRef, useState } from "react";

import IconButton from "../components/button/icon-button";
import { useCreateGraphic } from "../components/graphics/create-graphic";
import { GraphicListRef } from "../components/graphics/graphic-list";
import GraphicsPanel from "../components/graphics/graphics-panel";
import SearchInput from "../components/inputs/search-input";
import { BackgroundListRef } from "../components/lists/background/background-list";
import GridSizer from "../components/lists/grid-sizer";
import Panel from "../components/panels/v2/panel";
import PanelContent from "../components/panels/v2/panel-content";
import PanelFooter from "../components/panels/v2/panel-footer";
import PanelHeader from "../components/panels/v2/panel-header";
import { useCreateBackground } from "../hooks/use-create-background";
import useLocale from "../hooks/use-locale";
import AssetUploadModal from "../modals/asset-upload-modal";
import BackgroundsPanel from "../sections/backgrounds-panel";

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function CustomTabPanel(props: TabPanelProps): ReactElement {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && <>{children}</>}
    </div>
  );
}

type BackgroundsGraphicsAreaProps = {
  backgroundId: string | undefined;
  setBackgroundId: (id: string | undefined) => void;
  onGraphicAdded: (graphicId: string, width: number, height: number) => void;
};

export function BackgroundsGraphicsArea({
  backgroundId,
  setBackgroundId,
  onGraphicAdded,
}: BackgroundsGraphicsAreaProps): ReactElement {
  const TabIndexBackgrounds = 0;
  const backgroundListRef = useRef<BackgroundListRef>(null);
  const createBackground = useCreateBackground();

  const TabIndexGraphics = 1;
  const graphicsListRef = useRef<GraphicListRef>(null);
  const createGraphic = useCreateGraphic();

  const [selectedTabIndex, setSelectedTabIndex] = useState(TabIndexBackgrounds);
  const [searchQuery, setSearchQuery] = useState<string>("");
  const [isUploadOpen, setIsUploadOpen] = useState(false);
  const [gridSize, setGridSize] = useState<number>(100);

  const handleSelectedTabIndexChange = (
    event: React.SyntheticEvent,
    newValue: number,
  ): void => {
    setSelectedTabIndex(newValue);
  };

  const { getText } = useLocale();

  const createAsset =
    selectedTabIndex === TabIndexBackgrounds
      ? async (name: string, file: File) => {
          const response = await createBackground({
            input: {
              name,
              image: file,
            },
          });

          const graphQLError = response.errors?.at(0);
          if (graphQLError) {
            throw new Error();
          }

          const result = response.data?.createBackground;
          if (result?.__typename === "CreateBackgroundError") {
            throw new Error(result.message);
          }
        }
      : async (name: string, file: File) => {
          const response = await createGraphic({
            input: {
              name,
              file,
            },
          });

          const graphQLError = response.errors?.at(0);
          if (graphQLError) {
            throw new Error();
          }

          const result = response.data?.createGraphic;
          if (result?.__typename === "CreateGraphicError") {
            throw new Error(result.message);
          }
        };
  const assetUploadHeadline =
    selectedTabIndex === TabIndexBackgrounds
      ? getText("sections.assetUpload.background.headline")
      : getText("sections.assetUpload.graphic.headline");
  const formNameExample =
    selectedTabIndex === TabIndexBackgrounds
      ? getText("sections.assetUpload.background.form.nameExample")
      : getText("sections.assetUpload.graphic.form.nameExample");
  function handleCloseAssetUpload(): void {
    setIsUploadOpen(false);

    // Update backgrounds or graphics list
    switch (selectedTabIndex) {
      case TabIndexBackgrounds:
        backgroundListRef.current?.refetch();
        break;
      case TabIndexGraphics:
        graphicsListRef.current?.refetch();
        break;
      default:
        break;
    }
  }

  return (
    <section className="h-1/2">
      <Panel>
        <PanelHeader>
          <Tabs
            value={selectedTabIndex}
            onChange={handleSelectedTabIndexChange}
            aria-label="basic tabs example"
          >
            <Tab label={getText("sections.backgroundsPanel.headline")} />
            <Tab label={getText("sections.graphicsPanel.headline")} />
          </Tabs>
          <div className="flex flex-grow space-x-5">
            <SearchInput value={searchQuery} onChange={setSearchQuery} />
            <IconButton
              icon={<FileUploadOutlined className="!fill-companyMediumGrey" />}
              onClick={() => setIsUploadOpen(true)}
            />
          </div>
        </PanelHeader>
        <PanelContent>
          <CustomTabPanel value={selectedTabIndex} index={TabIndexBackgrounds}>
            <BackgroundsPanel
              selectedBackgroundId={backgroundId}
              selectedBackgroundIdChange={setBackgroundId}
              query={searchQuery}
              gridSize={gridSize}
              backgroundListRef={backgroundListRef}
            />
          </CustomTabPanel>
          <CustomTabPanel value={selectedTabIndex} index={TabIndexGraphics}>
            <GraphicsPanel
              handleGraphicClick={onGraphicAdded}
              query={searchQuery}
              gridSize={gridSize}
              graphicListRef={graphicsListRef}
            />
          </CustomTabPanel>
        </PanelContent>
        <PanelFooter>
          <GridSizer
            gridSize={gridSize}
            setGridSize={setGridSize}
            step={10}
            minGridSize={50}
            maxGridSize={150}
          />
        </PanelFooter>
      </Panel>
      <AssetUploadModal
        headline={assetUploadHeadline}
        open={isUploadOpen}
        onClose={() => handleCloseAssetUpload()}
        createAsset={createAsset}
        formNameExample={formNameExample}
      />
    </section>
  );
}
